import { observable, action } from 'mobx';
import { request } from 'utils/api';
import BaseStore from 'common/stores/BaseStore';

export default class PagesStore extends BaseStore {
  @observable register = new Map();
  @observable items = [];
  @observable totalItems = 0;
  @observable limit = 10;
  @observable page = 1;
  @observable venueId;
  @observable sort = {
    order: 'asc',
    field: 'createdAt',
  };

  parseItem(item) {
    return {
      ...item,
      createdAt: new Date(item.createdAt),
    };
  }

  @action
  setPage(page) {
    this.page = page;
  }

  @action
  setVenueId(id) {
    this.items.clear();
    this.register.clear();
    this.venueId = id;
  }

  get(id) {
    return this.register.get(id);
  }

  @action
  fetchItem(id, statusKey = `item:${id}`) {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: `/1/pages/${id}`,
    })
      .then(({ data }) => {
        const item = this.parseItem(data);
        this.register.set(item.id, item);
        status.success();
        return item;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  fetchItemByExternalId(
    externalId,
    statusKey = `item:externalId:${externalId}`
  ) {
    const status = this.createStatus(statusKey);
    return request({
      method: 'POST',
      path: '/1/pages/search',
      body: {
        externalId,
        limit: 1,
        sort: this.sort,
      },
    })
      .then(({ data }) => {
        if (!data || !data.length) {
          status.error(new Error('Not Found'));
          throw new Error('Not Found');
        }
        const item = this.parseItem(data[0]);
        this.register.set(item.id, item);
        status.success();
        return item;
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchItemBySlug(slug, statusKey = `item:${slug}`) {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: `/1/pages/slug/${slug}`,
    })
      .then(({ data }) => {
        status.success();
        return this.parseItem(data);
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }

  @action
  fetchItems(
    {
      externalId,
      venueId = this.venueId,
      limit = this.limit,
      skip = (this.page - 1) * this.limit,
    } = {},
    statusKey = 'list'
  ) {
    this.register.clear();
    const status = this.createStatus(statusKey);
    return request({
      method: 'POST',
      path: '/1/pages/search',
      body: {
        limit,
        skip,
        venueId,
        externalId,
        sort: this.sort,
      },
    })
      .then(({ data, meta }) => {
        const items = data.map((item) => this.parseItem(item));
        items.forEach((item) => {
          this.register.set(item.id, item);
        });
        this.totalItems = meta.total;
        this.items.replace(items);
        status.success();
        return items;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  update(body, statusKey = 'update') {
    const status = this.createStatus(statusKey);
    const { id, ...rest } = body;
    return request({
      method: 'PATCH',
      path: `/1/pages/${id}`,
      body: rest,
    })
      .then(({ data }) => {
        const item = this.parseItem(data);
        this.register.set(item.id, item);
        return this.fetchItems({}, false).then(() => {
          status.success();
          return item;
        });
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }
}
